<template>
    <v-form ref="treatmentComponentForm">
        <v-col v-if="variant === 'inline'" cols="12">
            <v-row v-if="!_.isNil(treatmentComponentFormConfig)" no-gutters>
                <v-col cols="4">
                    <v-autocomplete
                        :items="treatmentComponentFormConfig.treatmentComponentOptions"
                        label="Treatment Component (*)"
                        v-model="registryEntryId"
                        item-title="displayName"
                        item-value="id"
                        class="me-2"
                        :rules="requiredSimpleValueRules('Treatment Component')"
                    />
                </v-col>
                <v-col cols="2">
                    <v-text-field
                        :label="concentrationFieldLabel"
                        v-model="concentration"
                        class="me-2"
                        :rules="
                            requireConcentrationAndUnitsValues
                                ? requiredTextValueRules('Concentration')
                                : undefined
                        "
                    />
                </v-col>
                <v-col cols="3">
                    <v-autocomplete
                        :items="treatmentComponentFormConfig.unitsOptions"
                        :label="unitsFieldLabel"
                        v-model="unitsRegistryId"
                        item-title="displayName"
                        item-value="id"
                        class="me-6"
                        clearable
                        :rules="
                            requireConcentrationAndUnitsValues
                                ? requiredSimpleValueRules('Units')
                                : undefined
                        "
                    />
                </v-col>
                <v-col cols="2" align-self="center">
                    <v-row>
                        <v-btn
                            class="mb-3"
                            color="primary"
                            size="small"
                            @click="saveTreatmentComponent"
                        >
                            {{ submitButtonText }}
                        </v-btn>
                        <v-btn
                            class="mb-3 ms-2"
                            variant="elevated"
                            size="small"
                            @click="closeEditTreatmentComponent"
                        >
                            Cancel
                        </v-btn>
                    </v-row>
                </v-col>
            </v-row>
        </v-col>
        <v-card v-else>
            <v-card-title primary-title>{{ title }}</v-card-title>
            <v-card-text v-if="!_.isNil(treatmentComponentFormConfig)">
                <v-col cols="12">
                    <v-row>
                        <v-col cols="6">
                            <v-autocomplete
                                :items="treatmentComponentFormConfig.treatmentComponentOptions"
                                label="Treatment Component (*)"
                                clearable
                                v-model="registryEntryId"
                                item-title="displayName"
                                item-value="id"
                                :rules="requiredSimpleValueRules('Treatment Component')"
                            />
                        </v-col>
                        <v-col cols="3">
                            <v-text-field
                                :label="concentrationFieldLabel"
                                v-model="concentration"
                                type="number"
                                :rules="
                                    requireConcentrationAndUnitsValues
                                        ? requiredTextValueRules('Concentration')
                                        : undefined
                                "
                            />
                        </v-col>
                        <v-col cols="3">
                            <v-autocomplete
                                :items="treatmentComponentFormConfig.unitsOptions"
                                :label="unitsFieldLabel"
                                clearable
                                v-model="unitsRegistryId"
                                item-title="displayName"
                                item-value="id"
                                :rules="
                                    requireConcentrationAndUnitsValues
                                        ? requiredSimpleValueRules('Units')
                                        : undefined
                                "
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn color="primary" @click="saveTreatmentComponent"> {{ submitButtonText }} </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>
<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import _ from "lodash";
import {
    TreatmentComponentFormConfig,
    TreatmentComponentSetupConfig,
    treatmentsAPI,
} from "@/services/treatments/treatmentsAPI";
import { withLoadingScreen } from "@/lib/loading";
import { requiredSimpleValueRules, requiredTextValueRules } from "@/lib/validationUtils";
import { EventBus } from "@/lib/event-bus";

type TreatmentComponentFormVariant = "card" | "inline";

const { treatmentComponent, formConfig, variant } = defineProps<{
    treatmentComponent?: TreatmentComponentSetupConfig;
    formConfig?: TreatmentComponentFormConfig;
    variant?: TreatmentComponentFormVariant;
}>();

const emit = defineEmits<{
    (e: "onSaved", treatmentComponentSetupConfig: TreatmentComponentSetupConfig): void;
    (e: "onCancel"): void;
}>();

const treatmentComponentForm = ref();
const treatmentComponentFormConfig = ref<TreatmentComponentFormConfig | null>(formConfig ?? null);

onMounted(async () => {
    if (!formConfig) {
        await withLoadingScreen(async () => {
            treatmentComponentFormConfig.value =
                await treatmentsAPI.fetchTreatmentComponentFormConfig();
        });
    }
});

const registryEntryId = ref<string | null>(treatmentComponent?.registryEntryId ?? null);
const concentration = ref<string | null>(treatmentComponent?.concentration ?? null);
const unitsRegistryId = ref<string | null>(treatmentComponent?.unitsRegistryEntryId ?? null);

async function validate() {
    const form = treatmentComponentForm.value;
    const validationResult = await form.validate();
    if (!validationResult.valid) {
        const errors = validationResult.errors;
        const msgs = errors.map((error) => {
            return error.errorMessages.join(", ");
        });
        EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
        return false;
    } else {
        return true;
    }
}

function closeEditTreatmentComponent() {
    emit("onCancel");
}

async function saveTreatmentComponent() {
    if (!(await validate())) {
        return;
    }

    const existingTreatmentComponent = treatmentComponent ? { ...treatmentComponent } : {};

    emit("onSaved", {
        ...existingTreatmentComponent,
        registryEntryId: registryEntryId.value!,
        concentration: concentration.value,
        unitsRegistryEntryId: unitsRegistryId.value,
    });
}

const requireConcentrationAndUnitsValues = computed(() => {
    return (
        (!_.isNil(concentration.value) && concentration.value !== "") ||
        !_.isNil(unitsRegistryId.value)
    );
});

const conditionallyRequiredAsterisk = computed(() => {
    return requireConcentrationAndUnitsValues.value ? " (*)" : "";
});

const concentrationFieldLabel = computed(() => {
    return `Concentration ${conditionallyRequiredAsterisk.value}`;
});

const unitsFieldLabel = computed(() => {
    return `Units ${conditionallyRequiredAsterisk.value}`;
});

const title = computed(() => {
    const editOrAdd = treatmentComponent ? "Edit" : "Add";
    return `${editOrAdd} Treatment Component`;
});

const submitButtonText = computed(() => {
    return treatmentComponent ? "Save" : "Add";
});
</script>
